import Big from 'big.js';
import { ApiErrorResponse } from '../../../../services/apiError';
import { getFormPdf } from '../../services/AgendaService';

/** Mensaje por campo del request, tal como lo manda el backend en `details` ("customer.document", "paymentLines[0].amount"). */
export type FieldErrors = Record<string, string>;

export function fieldErrorsOf(apiError: ApiErrorResponse): FieldErrors {
  const errors: FieldErrors = {};
  for (const detail of apiError.details ?? []) {
    if (!errors[detail.field]) errors[detail.field] = detail.message;
  }
  return errors;
}

/** Los errores de negocio (4xx) traen un mensaje para el usuario; el resto, el genérico de la acción. */
export function errorMessageOf(apiError: ApiErrorResponse, fallback: string): string {
  return apiError.status >= 400 && apiError.status < 500 && apiError.message ? apiError.message : fallback;
}

/** Primer mensaje de los campos que empiezan con el prefijo ("paymentLines", "invoices"). */
export function firstErrorWithPrefix(errors: FieldErrors, prefix: string): string | undefined {
  const key = Object.keys(errors).find((field) => field === prefix || field.startsWith(`${prefix}[`) || field.startsWith(`${prefix}.`));
  return key ? errors[key] : undefined;
}

export function toBig(value: number | null | undefined): Big {
  return new Big(value ?? 0);
}

/** Abre el PDF del comprobante en otra pestaña. */
export async function openFormPdf(adminFormId: number): Promise<void> {
  const blob = await getFormPdf(adminFormId);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  // La pestaña nueva ya cargó el PDF; el objeto se libera después para no retener memoria
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/** Fecha para la API (yyyy-MM-dd), en la zona del navegador. */
export function toApiDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Fecha de la API (yyyy-MM-dd) como fecha local, sin correrla por la zona horaria. */
export function fromApiDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [year, month, day] = value.substring(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** "1/5/2026", el formato corto de fechas del design system. */
export function formatShortDate(value: string | null | undefined): string {
  const date = fromApiDate(value);
  return date ? date.toLocaleDateString('es-AR') : '—';
}

/** Abre en otra pestaña un PDF que vino en base64 (el de autorización de la orden). */
export function openBase64Pdf(base64: string): void {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  window.open(url, '_blank', 'noopener');
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}

let rowSequence = 0;

/** Identificador de una fila nueva de la pantalla (las del back traen el suyo). */
export function newRowKey(prefix: string): string {
  rowSequence += 1;
  return `${prefix}-${Date.now()}-${rowSequence}`;
}
